import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { MenuItem } from '@mui/material';
import {
  createColumnHelper,
  flexRender,
  type RowPinningState,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import {
  ArrowDown,
  ArrowUp,
  EllipsisVertical,
  Pencil,
  Pin,
  PinOff,
  Trash2,
  UserRoundPlus,
} from 'lucide-react';

import {
  type TypeProject,
  type TypeProjectMember,
} from '../../common/dataTypes';
import { createTanstackTable } from '../../common/utils';
import {
  usePinProject,
  useUnpinProject,
} from '../../hooks/tanstack/useProjectsHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import {
  isOrgAdmin,
  useGetRoles,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import RedPin from '../../icons/RedPin';
import { type Author, type ProjectRole } from '../../types';
import cn from '../../utils/cn';
import {
  useCurrentMembership,
  useCurrentOrganization,
} from '../../utils/helpers';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import MenuDropdown from '../MenuDropdown';
import Spinner from '../Spinner';
import WarningDialog from '../WarningDialog';

import CreateProjectDialog from './CreateProjectDialog';
import InviteProjectFormDialog from './InviteProjectFormDialog';
import ProjectAccessDeniedDialog from './ProjectAccessDeniedDialog';

export type ProjectRow = {
  id: number;
  name: string;
  description: string | undefined;
  status: string;
  role: ProjectRole;
  isGuestProject: boolean;
  shared_by: Author;
  modified: string | undefined;
  project: TypeProject;
  isPinned?: boolean;
  updatedAt: string;
  updatedBy: string;
  owners: string[];
  members: TypeProjectMember[];
};

const defaultData: ProjectRow[] = [];

const columnHelper = createColumnHelper<ProjectRow>();

const columns = ({ isGuestProject }: { isGuestProject: boolean }) => [
  columnHelper.accessor('name', {
    cell: (info) => {
      const isPinned = info.row.getIsPinned();

      return (
        <div className="w-full group flex items-center gap-2 py-3 text-start">
          <p className="line-clamp-2">{info.getValue()}</p>
          <div>{isPinned && <RedPin />}</div>
        </div>
      );
    },
    header: () => (
      <div className="flex items-center gap-2">
        <p>Name</p>
      </div>
    ),
  }),
  columnHelper.accessor('description', {
    id: 'description',
    cell: (info) => {
      const value = info.getValue();

      return (
        <div className="line-clamp-2 max-w-[250px] 2xl:max-w-[350px]">
          {value}
        </div>
      );
    },
    header: () => <div>Description</div>,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    cell: (info) => {
      const isGuestProject = info.row.original.isGuestProject;
      const sharedBy = info.row.original.shared_by;

      const status = info.getValue();

      if (isGuestProject) {
        return <p>{sharedBy?.full_name}</p>;
      }

      return status ? (
        <div className="min-w-[100px]">
          {status !== 'completed' && (
            <div className="flex items-center justify-center rounded-full bg-[#FFF4F2] p-1 text-sm font-bold text-primary-red">
              Not Ready
            </div>
          )}
          {status === 'completed' && (
            <div className="flex items-center justify-center rounded-full bg-[#E8F2FF] p-1 text-sm font-bold text-[#4782D6]">
              Ready
            </div>
          )}
        </div>
      ) : (
        <div className="text-gray-400 text-start">--</div>
      );
    },
    header: (info) =>
      isGuestProject ? <div>Shared by</div> : <div>Status</div>,
  }),
  columnHelper.accessor('modified', {
    id: 'modified',
    cell: (info) => {
      const value = info.getValue();
      const updatedBy = info.row.original.updatedBy;

      return (
        <div className="min-w-[100px]">
          {value ? value + (updatedBy ? ' by ' + updatedBy : '') : '--'}
        </div>
      );
    },
    sortingFn: (a, b) =>
      new Date(a.original.modified as string).getTime() -
      new Date(b.original.modified as string).getTime(),
    header: () => <div>Last Modified</div>,
  }),
  columnHelper.accessor('owners', {
    id: 'owners',
    cell: (info) => {
      const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

      const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] =
        useState(false);

      const [openCreateProjectDialog, setOpenCreateProjectDialog] =
        useState(false);

      const [openInviteFormDialog, setOpenInviteFormDialog] = useState(false);

      const { orgId } = useGetRouteParams();

      const open = Boolean(anchorEl);

      const { id, role, members, name, description, updatedAt } =
        info.row.original;

      const isPinned = info.row.original.isPinned;

      const { organization: orgMembership } = useCurrentMembership();

      const { mutate: pinProject } = usePinProject();

      const { mutate: unpinProject } = useUnpinProject();

      const handleCloseDropdown = () => setAnchorEl(null);

      const handleOpenConfirmDeleteDialog = () => {
        setOpenConfirmDeleteDialog(true);
      };

      const handleCloseConfirmDeleteDialog = () => {
        setOpenConfirmDeleteDialog(false);
      };

      const handleOpenCreateProjectDialog = () => {
        setOpenCreateProjectDialog(true);
      };

      const handleCloseCreateProjectDialog = () => {
        setOpenCreateProjectDialog(false);
      };

      const handleOpenInviteFormDialog = () => {
        setOpenInviteFormDialog(true);
      };

      const handleCloseInviteFormDialog = () => {
        setOpenInviteFormDialog(false);
      };

      const handlePinRow = () => {
        info.row.pin('top');
        pinProject({
          orgId,
          projectId: id,
        });
      };

      const handleUnpinRow = () => {
        info.row.pin(false);
        unpinProject({
          orgId,
          projectId: id,
        });
      };

      const isAdmin = orgMembership && isOrgAdmin(orgMembership.role);

      return (
        <div className="flex min-w-[200px] justify-between gap-6">
          <p className="max-w-[180px] grow truncate">
            {info.getValue().length ? info.getValue().join(', ') : ' --'}
          </p>
          {(role === 'project_owner' || isAdmin) && (
            <MenuDropdown
              trigger={
                <button>
                  <EllipsisVertical width={20} height={20} />
                </button>
              }
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              style={{
                marginTop: '5px',
              }}
              open={open}
            >
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseDropdown();
                  handleOpenInviteFormDialog();
                }}
              >
                <div className="flex items-center space-x-2">
                  <UserRoundPlus size={15} />
                  <p>Add members</p>
                </div>
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isPinned) {
                    handlePinRow();
                  } else {
                    handleUnpinRow();
                  }
                  handleCloseDropdown();
                }}
              >
                {isPinned ? (
                  <div className="flex items-center space-x-2">
                    <PinOff size={15} />
                    <p>Unpin project</p>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Pin size={15} />
                    <p>Pin project</p>
                  </div>
                )}
              </MenuItem>
              {!isGuestProject && (
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseDropdown();
                    handleOpenCreateProjectDialog();
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <Pencil size={15} />
                    <p>Edit</p>
                  </div>
                </MenuItem>
              )}
              <div className="!border-t !border-[#E4E7EC]" />
              {!isGuestProject && isAdmin && (
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseDropdown();
                    handleOpenConfirmDeleteDialog();
                  }}
                >
                  <div className="flex items-center space-x-2 text-primary-red">
                    <Trash2 size={15} />
                    <p>Delete</p>
                  </div>
                </MenuItem>
              )}
            </MenuDropdown>
          )}
          {members && (
            <InviteProjectFormDialog
              project={info.row.original.project}
              showTrigger={false}
              projectMembers={members}
              inviteUserMode="multiple"
              open={openInviteFormDialog}
              handleClose={handleCloseInviteFormDialog}
              handleOpen={handleOpenInviteFormDialog}
            />
          )}
          <CreateProjectDialog
            open={openCreateProjectDialog}
            defaultValues={{
              id,
              name,
              description,
            }}
            handleClose={handleCloseCreateProjectDialog}
          />
          <ConfirmDeleteDialog
            type="project"
            row={{
              id,
              name,
              description,
              modified: updatedAt,
            }}
            open={openConfirmDeleteDialog}
            handleClose={handleCloseConfirmDeleteDialog}
          />
        </div>
      );
    },
    header: () => <div>Project Owners</div>,
  }),
];

const ProjectsTable = ({
  defaultProjects,
  isLoading,
  isHidingUnpinnedProjects = false,
}: {
  isLoading: boolean;
  isHidingUnpinnedProjects: boolean;
  defaultProjects: TypeProject[] | undefined;
}) => {
  const [data, setData] = useState<ProjectRow[]>(defaultData);
  const [openDialog, setOpenDialog] = useState(false);
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(NaN);

  const [rowPinning, setRowPinning] = useState<RowPinningState>({
    top: [],
    bottom: [],
  });

  const form = useFormContext();

  const { watch } = form;

  const searchText = watch('search');

  const navigate = useNavigate();

  const { data: organization } = useCurrentOrganization();

  const { orgRole } = useGetRoles();

  const isAdmin = orgRole && isOrgAdmin(orgRole);

  const { data: currentUser, isLoading: isLoadingCurrentUser } = useGetUser();

  const handleSelect = (id: number) => {
    const selectedProject = defaultProjects?.find(
      (project) => project.id === id
    );

    const selectedProjectUser = selectedProject?.members.find(
      (m) => m.user.id === currentUser?.id
    );

    const isAuthenticated =
      selectedProjectUser?.role === 'project_owner' &&
      selectedProject?.created_by.email === selectedProjectUser.user.email;

    if (!selectedProject) {
      return;
    }

    if (
      !isAuthenticated &&
      selectedProject?.onboarding_status !== 'completed'
    ) {
      handleOpenWarningDialog();
      return;
    }

    if (
      organization &&
      isOrgAdmin(organization.role) &&
      !selectedProject.role
    ) {
      setSelectedProjectId(id);
      setOpenDialog(true);
      return;
    }

    if (selectedProject.onboarding_status === 'configuring') {
      navigate(`create/?step=2&project_id=${id}`);
      return;
    }

    if (selectedProject.onboarding_status === 'uploading_data') {
      navigate(`create/?step=3&project_id=${id}`);
      return;
    }

    if (selectedProject.onboarding_status === 'reviewing') {
      navigate(`create/?step=4&project_id=${id}`);
      return;
    }

    navigate(id.toString());
  };

  const table = createTanstackTable<ProjectRow>({
    data,
    rowPinning,
    setRowPinning,
    enableRowPinning: true,
    keepPinnedRows: false,
    columns: columns({
      isGuestProject: orgRole === 'guest_organization_member',
    }),
    defaultSorting: {
      id: 'modified',
      desc: true,
    },
  });

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenWarningDialog = () => {
    setOpenWarningDialog(true);
  };

  const handleCloseWarningDialog = () => {
    setOpenWarningDialog(false);
  };

  useEffect(() => {
    if (defaultProjects && defaultProjects?.length > 0) {
      let filteredProjects = defaultProjects;

      if (isHidingUnpinnedProjects) {
        filteredProjects = filteredProjects.filter(
          (project) => project.is_pinned
        );
      }

      setData(
        filteredProjects.map((project) => ({
          id: project.id,
          name: project.name,
          status: project.onboarding_status,
          description: project.description,
          role: project.role,
          isGuestProject: project.is_guest_project,
          shared_by: project.shared_by,
          project,
          isPinned: project.is_pinned,
          modified: format(project.updated_at ?? '', 'y/MM/dd hh:mm a'),
          updatedAt: project.updated_at,
          updatedBy: project.updated_by?.full_name,
          members: project.members,
          owners: project.members
            .filter((m) => m.role === 'project_owner')
            .map((member) => member.user.full_name),
        }))
      );
      return;
    }

    setData([]);
  }, [defaultProjects, isHidingUnpinnedProjects]);

  useEffect(() => {
    if (!data?.length) {
      return;
    }

    const currentRows = table.getRowModel().rows;

    const pinnedIds = new Set(data.filter((d) => d.isPinned).map((d) => d.id));

    const pinnedRowIndexes = currentRows
      .filter((row) => pinnedIds.has(row.original.id))
      .map((row) => row.index.toString());

    setRowPinning((prev) => {
      const prevTop = prev?.top?.join(',');
      const newTop = pinnedRowIndexes.join(',');

      if (prevTop === newTop) {
        return prev;
      }
      return { top: pinnedRowIndexes };
    });
  }, [data, table]);

  return (
    <div>
      <table className="w-full relative">
        <thead className="sticky top-0 z-30">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={cn(
                    'first:border-l bg-gray-100 z-30 border-t border-b last:border-r border-[#E4E7EC] px-6 py-3 text-start text-sm text-[#4D4D4D]'
                  )}
                >
                  <button
                    type="button"
                    onClick={header.column.getToggleSortingHandler()}
                    className="flex gap-1 items-start"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {{
                      asc: <ArrowUp size={20} />,
                      desc: <ArrowDown size={20} />,
                    }[header.column.getIsSorted() as string] ?? null}
                  </button>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="relative">
          {(isLoading || isLoadingCurrentUser) && (
            <div
              className={cn(
                'absolute border-[#E4E7EC] h-[200px] z-50 border flex w-full items-center justify-center bg-gray-200 opacity-50',
                {
                  'h-full': table.getRowModel().rows.length > 0,
                }
              )}
            >
              <Spinner />
            </div>
          )}
          {[...table.getTopRows(), ...table.getCenterRows()].map((row) => {
            const isAllowed = defaultProjects?.find(
              (p) => p.id === row.original.id
            )?.role;

            return (
              <tr
                key={row.id}
                className={cn('cursor-pointer text-[#333] transition py-3', {
                  '!text-[#999999] !bg-[#FFFFFF] disabled': !isAllowed,
                  'group hover:bg-[#F8F8F8]': isAllowed,
                })}
              >
                {row.getVisibleCells().map((cell, index) => (
                  <td
                    key={cell.id}
                    onClick={() => {
                      index !== 4 && handleSelect(row.original.id);
                    }}
                    className={cn(
                      ' border-[#E4E7EC] border-t border-b pl-6 last:pr-6 first:border-l last:border-r'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!table.getRowModel().rows.length && !isLoading && (
        <div className="flex justify-center border border-t-0 border-[#E4E7EC] py-20">
          <p className="text-lg text-[#999]">
            {isAdmin
              ? searchText
                ? 'No results'
                : 'No projects available. Please create a project.'
              : 'You haven’t been invited to work in any projects yet'}
          </p>
        </div>
      )}
      <WarningDialog
        title="Access Denied"
        open={openWarningDialog}
        primaryActionText={'Ok'}
        handlePrimaryAction={handleCloseWarningDialog}
        content={
          'This project is not ready for you to access yet. Please wait for the project owner to finish setting up the project.'
        }
      />
      <ProjectAccessDeniedDialog
        iProjectId={selectedProjectId}
        open={openDialog}
        handleOnClose={handleCloseDialog}
        handleOnSubmit={() => {
          navigate(`${selectedProjectId}`);
        }}
      />
    </div>
  );
};

export default ProjectsTable;
